import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useJobExecution, type JobExecutionResponse } from '../api/jobs'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Separator } from './ui/separator'
import { ScrollArea } from './ui/scroll-area'
import { AppIcon } from './ui/icon'
import { mdiCounter, mdiChevronDown, mdiChevronRight } from '@mdi/js'
import { EmptyState } from './ui/empty-state'

interface LatestExecutionLogsProps {
  org: string
  jobUid: string
  execution: JobExecutionResponse
  limit?: number
}

export function LatestExecutionLogs({ org, jobUid, execution, limit = 50 }: LatestExecutionLogsProps) {
  const { data: executionWithLogs } = useJobExecution(org, jobUid, execution.uid, limit)
  const [expandedLogs, setExpandedLogs] = useState<Set<number>>(new Set())
  const { t } = useTranslation('jobs')

  const toggleLog = (index: number) => {
    setExpandedLogs(prev => {
      const next = new Set(prev)
      if (next.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
  }

  const formatLogAttrs = (attrs: unknown) => {
    if (!attrs || typeof attrs !== 'object' || attrs === null || Object.keys(attrs).length === 0) {
      return null
    }

    // Format as simple "key: value" pairs for first level
    const pairs = Object.entries(attrs).map(([key, value]) => {
      if (typeof value === 'object' && value !== null) {
        return `${key}: ${JSON.stringify(value)}`
      }
      return `${key}: ${value}`
    })

    return pairs.join(', ')
  }

  const getLogLevelLabel = (level: number) => {
    if (level >= 8) return 'ERROR'
    if (level >= 4) return 'WARN'
    if (level >= 0) return 'INFO'
    return 'DEBUG'
  }

  const getLogLevelColor = (level: number) => {
    if (level >= 8) return 'text-destructive bg-destructive/10 border-destructive/20'
    if (level >= 4) return 'text-yellow-600 dark:text-yellow-400 bg-yellow-500/10 border-yellow-500/20'
    if (level >= 0) return 'text-primary bg-primary/10 border-primary/20'
    return 'text-muted-foreground bg-muted border-border'
  }

  if (!executionWithLogs?.logs || executionWithLogs.logs.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AppIcon path={mdiCounter} size="md" />
            {t('latest_logs.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState variant="inline" className="py-4" title={t('latest_logs.no_logs')} />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AppIcon path={mdiCounter} size="md" />
          {t('latest_logs.title_with_count', { count: executionWithLogs.logs.length })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea>
          <div className="space-y-1">
            {executionWithLogs.logs.map((log: { level: number; time: string; message: string; attrs?: unknown }, index: number) => {
              const isExpanded = expandedLogs.has(index)
              return (
                <div
                  key={index}
                  className={`border rounded p-2 ${getLogLevelColor(log.level as number)}`}
                  data-testid={`log-row-${index}`}
                >
                  <div
                    className="flex items-center gap-2 cursor-pointer select-none"
                    onClick={() => toggleLog(index)}
                  >
                    <AppIcon
                      path={isExpanded ? mdiChevronDown : mdiChevronRight}
                      size="xs"
                      className="shrink-0 opacity-60"
                    />
                    <span className="text-xs font-mono text-muted-foreground whitespace-nowrap">
                      {new Date(log.time as string).toLocaleTimeString()}
                    </span>
                    <Badge variant="outline" className="text-xs px-1 py-0">
                      {getLogLevelLabel(log.level as number)}
                    </Badge>
                    <span className="text-sm flex-1">{log.message as string}</span>
                    {!isExpanded && formatLogAttrs(log.attrs) && (
                      <span className="text-xs font-mono text-muted-foreground ml-2 truncate max-w-48">
                        {formatLogAttrs(log.attrs)}
                      </span>
                    )}
                  </div>
                  {isExpanded && !!(log.attrs && typeof log.attrs === 'object' && log.attrs !== null && Object.keys(log.attrs as object).length > 0) && (
                    <div className="mt-2 space-y-1">
                      {Object.entries(log.attrs as Record<string, unknown>).map(([key, value]) => {
                        // Try to parse JSON strings for pretty display
                        let parsedJson: unknown = null
                        if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
                          try { parsedJson = JSON.parse(value) } catch { /* not JSON */ }
                        }

                        return (
                          <div key={key} className="text-xs font-mono">
                            <span className="font-semibold text-muted-foreground">{key}:</span>{' '}
                            {parsedJson !== null ? (
                              <pre className="bg-background/50 rounded p-1 mt-0.5 overflow-x-auto whitespace-pre">{JSON.stringify(parsedJson, null, 2)}</pre>
                            ) : typeof value === 'string' && value.includes('\n') ? (
                              <pre className="bg-background/50 rounded p-1 mt-0.5 overflow-x-auto whitespace-pre-wrap break-words">{value}</pre>
                            ) : typeof value === 'object' && value !== null ? (
                              <pre className="bg-background/50 rounded p-1 mt-0.5 overflow-x-auto whitespace-pre">{JSON.stringify(value, null, 2)}</pre>
                            ) : (
                              <span>{String(value ?? '')}</span>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </ScrollArea>

        <Separator className="my-4" />

        <div className="text-sm text-muted-foreground text-center">
          {t('latest_logs.showing', { count: executionWithLogs.logs.length })}
          {executionWithLogs.logs.length >= limit && (
            <div className="mt-1">
              <Link
                to="/$org/jobs/$jobUid/executions/$executionUid"
                params={{ org, jobUid, executionUid: execution.uid }}
                search={{ logs_limit: 500 }}
              >
                <Button variant="link" size="sm" className="p-0 h-auto text-primary">
                  {t('latest_logs.view_all', { count: limit })}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}